"use client";

import { api } from "@convex/_generated/api";
import { useAction } from "convex/react";
import { useEffect, useEffectEvent, useState } from "react";
import type { UserProfile } from "../utils";

type LiveStreamSectionProps = {
  profile: UserProfile;
};

type StreamStatus = {
  twitch: boolean;
  youtube: boolean;
};

export function LiveStreamSection({ profile }: LiveStreamSectionProps) {
  const [streamStatus, setStreamStatus] = useState<StreamStatus>({
    twitch: false,
    youtube: false,
  });
  const [isLoading, setIsLoading] = useState(true);

  const getLiveStatuses = useAction(api.twitch.getLiveStatuses);

  const checkStreamStatus = useEffectEvent(async () => {
    const status = await getLiveStatuses({ userId: profile._id });
    setStreamStatus(status);
    setIsLoading(false);
  });

  useEffect(() => {
    const hasStreamingPlatforms = Boolean(
      profile.twitchUsername || profile.youtubeChannelId
    );

    if (!hasStreamingPlatforms) {
      setIsLoading(false);
      return;
    }

    // Check immediately
    checkStreamStatus();

    // Poll every 60 seconds
    const POLL_INTERVAL = 60_000;
    const interval = setInterval(checkStreamStatus, POLL_INTERVAL);

    return () => {
      clearInterval(interval);
    };
  }, [profile.twitchUsername, profile.youtubeChannelId, checkStreamStatus]);

  const isLive = streamStatus.twitch || streamStatus.youtube;

  if (isLoading || !isLive) {
    return null;
  }

  const domain =
    typeof window !== "undefined"
      ? window.location.hostname
      : "bf6loadouts.com";

  return (
    <div className="space-y-4 rounded-[10px] border border-[#262A2D] bg-[#0C0D0E] p-6 shadow-2xl">
      <div className="flex items-center gap-3">
        <h2 className="font-bold font-heading text-2xl text-[#EAEBEC] tracking-wide">
          LIVE NOW
        </h2>
        <span className="inline-flex items-center gap-1.5 rounded-full border border-[#a970d6]/35 bg-[#a970d6]/10 px-2.5 py-1 font-mono text-[#a970d6] text-[10px] tracking-wider">
          <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-[#a970d6]" />
          LIVE
        </span>
      </div>

      <div className="space-y-6">
        {streamStatus.twitch && profile.twitchUsername && (
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <svg
                aria-label="Twitch"
                className="h-5 w-5"
                fill="currentColor"
                role="img"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <title>Twitch</title>
                <path d="M11.571 4.714h1.715v5.143H11.57zm4.715 0H18v5.143h-1.714zM6 0L1.714 4.286v15.428h5.143V24l4.286-4.286h3.428L22.286 12V0zm14.571 11.143l-3.428 3.428h-3.429l-3 3v-3H6.857V1.714h13.714Z" />
              </svg>
              <h3 className="font-semibold">Twitch</h3>
            </div>
            <div className="aspect-video overflow-hidden rounded-lg">
              <iframe
                allowFullScreen
                className="h-full w-full"
                src={`https://player.twitch.tv/?channel=${profile.twitchUsername}&parent=${domain}`}
                title={`${profile.twitchUsername}'s Twitch stream`}
              />
            </div>
          </div>
        )}

        {streamStatus.youtube && profile.youtubeChannelId && (
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <svg
                aria-label="YouTube"
                className="h-5 w-5"
                fill="currentColor"
                role="img"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <title>YouTube</title>
                <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
              </svg>
              <h3 className="font-semibold">YouTube</h3>
            </div>
            <div className="aspect-video overflow-hidden rounded-lg">
              <iframe
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="h-full w-full"
                src={`https://www.youtube.com/embed/live_stream?channel=${profile.youtubeChannelId}`}
                title={`${profile.username}'s YouTube stream`}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
